import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    ArrowLeft, Mail, Phone, Calendar, MapPin,
    ShoppingBag, DollarSign, Clock, CreditCard
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatCurrency, formatDate, getInitials, cn, getStatusColor } from '@utils';
import api from '@services/api';
import type { Order } from '@types';

interface CustomerDetail {
    id: number;
    name: string;
    email: string;
    phone?: string;
    address?: string;
    totalOrders: number;
    totalSpent: number;
    lastOrderAt?: string;
    createdAt: string;
    orders: Order[];
}

export default function CustomerDetailPage() {
    const { id } = useParams<{ id: string }>(); // assuming route is /customers/:email or :id, but backend uses email lookups usually for customers if they are guest? 
    // API route /customers/{email} or {id}? 
    // Let's assume the route uses email or ID. In App.tsx I didn't define the param name yet, I need to check.
    // Actually I haven't defined the route '/customers/:id' in App.tsx yet. I will do that.

    const { data, isLoading } = useQuery({
        queryKey: ['customer', id],
        queryFn: () => api.get<{ data: CustomerDetail }>(`/customers/${id}`),
        enabled: !!id,
    });

    const customer = data?.data;

    if (isLoading) {
        return (
            <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
                <div className="h-40 bg-gray-200 rounded-xl dark:bg-gray-700" />
                <div className="h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="text-center py-12">
                <h2 className="text-xl font-semibold">Customer not found</h2>
                <Link to="/customers" className="btn-primary mt-4 inline-flex">Back to Customers</Link>
            </div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Link to="/customers" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{customer.name}</h1>
                    <p className="text-gray-500 dark:text-gray-400">Customer Profile</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sidebar Info */}
                <div className="space-y-6">
                    {/* Profile Card */}
                    <div className="card p-6">
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="w-20 h-20 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 text-2xl font-bold mb-4">
                                {getInitials(customer.name)}
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{customer.name}</h2>
                            <div className="text-sm text-gray-500 mt-1">
                                Joined {formatDate(customer.createdAt)}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3 text-sm">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600 dark:text-gray-300">{customer.email}</span>
                            </div>
                            {customer.phone && (
                                <div className="flex items-center gap-3 text-sm">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-600 dark:text-gray-300">{customer.phone}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="card p-6 space-y-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white">Lifetime Stats</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">Total Spent</div>
                                <div className="text-lg font-bold text-success-600">{formatCurrency(customer.totalSpent)}</div>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">Total Orders</div>
                                <div className="text-lg font-bold text-gray-900 dark:text-white">{customer.totalOrders}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content - Orders */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="card">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold text-gray-900 dark:text-white">Order History</h3>
                        </div>
                        {customer.orders?.length === 0 ? (
                            <div className="p-12 text-center text-gray-500">
                                <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No orders yet.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                {customer.orders?.map((order) => (
                                    <div key={order.orderNumber} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={cn(
                                                "w-10 h-10 rounded-full flex items-center justify-center",
                                                order.status === 'completed' ? "bg-success-100 text-success-600" : "bg-gray-100 text-gray-500"
                                            )}>
                                                <ShoppingBag className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-white">Order #{order.orderNumber}</div>
                                                <div className="text-sm text-gray-500">
                                                    {order.event?.name} • {formatDate(order.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-medium text-gray-900 dark:text-white">{formatCurrency(order.totalAmount, order.currency)}</div>
                                            <span className={cn('text-xs capitalize', getStatusColor(order.status))}>
                                                {order.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
